import React, { useContext, useEffect, useState } from "react";
import { titleCase } from "../../../../Utility";
import {
  isMobile,
  currentRequestTimeStamp,
  Activity,
} from "../../../../Constants";
import Axios from "../../../../Axios";
import { Urls } from "../../../../Urls";
import { toast } from "react-toastify";

import axios from "../../../../Axios/Axios";
import Loader from "../../../Loader";
import { DataGhost } from "../../../Planning/components/EmptyState";
import { useMixPanelForTracking } from "../../../../utils/TrackMixPanel";
import { UserApiContext } from "../../../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../../../MyCompanyProfileContext";
import NewProfileModal from "../../../Shared/NewTransporterProfileModal/NewProfileModal";
import styles from "./RequestApproval.module.css";

const getLastRate = (transporter) => {
  const rateList = transporter?.rate_list || [];
  return rateList.length ? rateList[rateList.length - 1] : null;
};

const isAcceptedApprovalPendingTransporter = (transporter) => {
  const lastRate = getLastRate(transporter);
  return (
    lastRate &&
    lastRate.bid_status === "accepted" &&
    (transporter.main_status === "approval_pending" ||
      lastRate.main_status === "approval_pending" ||
      (transporter.main_status !== "approved" && lastRate.main_status !== "approved"))
  );
};

const isApprovableTransporter = isAcceptedApprovalPendingTransporter;

const getMatchingTransporters = (item) => {
  return (item.transporter_list || item.transporter_rate_list || [])
    .slice()
    .sort((a, b) => {
      const aApprovable = isApprovableTransporter(a);
      const bApprovable = isApprovableTransporter(b);
      if (aApprovable !== bApprovable) return aApprovable ? -1 : 1;
      const aRate = Number(String(getLastRate(a)?.rate ?? a.rate ?? '').replace(/,/g, '')) || Infinity;
      const bRate = Number(String(getLastRate(b)?.rate ?? b.rate ?? '').replace(/,/g, '')) || Infinity;
      return aRate - bRate;
    });
};

const getTenure = (tenure) => {
  if (!tenure) return "NA";
  if (typeof tenure === "string") return tenure;
  if (Array.isArray(tenure) && tenure.length > 0) {
    return tenure[0].label || tenure[0].value || "NA";
  }
  if (typeof tenure === "object") return tenure.label || tenure.value || "NA";
  return "NA";
};

const getTruckLabel = (truckTypes) => {
  if (!truckTypes) return "NA";
  if (Array.isArray(truckTypes)) {
    const labels = truckTypes
      .map((truck) => truck?.label || truck?.value || truck)
      .filter(Boolean);
    return labels.length ? labels.join(", ") : "NA";
  }
  return truckTypes?.label || truckTypes?.value || truckTypes || "NA";
};

const getLaneData = (item) => {
  const lane = item.lane_details || item;
  const origin = lane.origin || item.origin;
  const destination = lane.destination || item.destination;
  const truckTypes = lane.truck_types || item.truck_types;

  return {
    originName:
      origin?.location?.location ||
      origin?.location_name ||
      item.origin?.location?.location ||
      item.origin?.location_name ||
      "NA",
    destName:
      destination?.location?.location ||
      destination?.location_name ||
      item.destination?.location?.location ||
      item.destination?.location_name ||
      "NA",
    truckLabel: getTruckLabel(truckTypes),
    truck_required: lane.truck_required || item.truck_required || "NA",
    contract_tenure: lane.contract_tenure || item.contract_tenure,
    due_date: lane.due_date || item.due_date || "NA",
  };
};

const HeaderCell = ({ width, children, className = "" }) => (
  <div className={`${styles.headerCell} ${className}`} style={{ width }}>
    {children}
  </div>
);

const RequestApproval = ({
  requestapprovallist = [],
  approverList = [],
}) => {
  const [approvalList, setApprovalList] = useState([]);
  const [show, setshow] = useState(false);
  const [finalPayload, setFinalPayload] = useState({});
  const [remarks, setRemarks] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [activeTransporter, setActiveTransporter] = useState({ id: "", name: "" });
  const [openProfileModal, setOpenProfileModal] = useState(false);

  const handleOpenProfile = (e, transporter) => {
    e.stopPropagation();
    setActiveTransporter({
      id: transporter.transporter_id,
      name: transporter.transporter_name,
    });
    setOpenProfileModal(true);
  };

  const handleCloseProfileModal = () => {
    setOpenProfileModal(false);
    setActiveTransporter({ id: "", name: "" });
  };

  const toggleExpand = (enquiryId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(enquiryId)) {
        next.delete(enquiryId);
      } else {
        next.add(enquiryId);
      }
      return next;
    });
  };

  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);

  useEffect(() => {
    if (!requestapprovallist || requestapprovallist.length === 0) {
      setApprovalList([]);
      setFinalPayload({});
      setshow(false);
      return;
    }

    setshow(true);
    TrackMixPanel({
      ModuleName: "lorri spot",
      ScreenName: "my enquiries - request approval",
      Data: "None",
      Definition: "user clicked on request approval button",
    });

    const promises = requestapprovallist.map((item) =>
      axios.get(`${Urls.spot}?field=get_enquiry&truck_enquiry_id=${item.truck_enquiry_id}`)
    );

    Promise.all(promises)
      .then((results) => {
        const newList = [];
        const initialFinalPayload = {};

        results.forEach((res, index) => {
          const enquiryId = requestapprovallist[index].truck_enquiry_id;
          const enquiryData = res.data.enquiry_lane_details
            ? res.data.enquiry_lane_details[0]
            : res.data;
          const tList = enquiryData?.transporter_list || enquiryData?.transporter_rate_list || [];
          const selectedTransporters = tList.map((transporter) => transporter.transporter_id);

          if (tList.length > 0) {
            if (selectedTransporters.length > 0) {
              initialFinalPayload[enquiryId] = selectedTransporters;
            }
            newList.push({
              ...requestapprovallist[index],
              ...enquiryData,
              truck_enquiry_id: enquiryData.truck_enquiry_id || enquiryId,
              transporter_list: tList,
            });
          }
        });

        newList.sort((a, b) => {
          const aTransporters = getMatchingTransporters(a);
          const bTransporters = getMatchingTransporters(b);
          const aTime = new Date(getLastRate(aTransporters[0])?.accepted_at || 0).getTime();
          const bTime = new Date(getLastRate(bTransporters[0])?.accepted_at || 0).getTime();
          return bTime - aTime;
        });

        setFinalPayload(initialFinalPayload);
        setApprovalList(newList);
        setshow(false);
      })
      .catch((err) => {
        setshow(false);
        console.log(err, "54");
      });
  }, [requestapprovallist]);

  const changeHandler = (e, item, transporter) => {
    const enquiryId = item.truck_enquiry_id;
    const transporterId = transporter.transporter_id;
    const key = `${enquiryId}_${transporterId}`;

    setFinalPayload((prevPayload) => {
      const selectedTransporters = prevPayload[enquiryId] || [];
      const nextPayload = { ...prevPayload };

      if (e.target.checked) {
        nextPayload[enquiryId] = Array.from(
          new Set([...selectedTransporters, transporterId])
        );
      } else {
        const updatedTransporters = selectedTransporters.filter(
          (id) => id !== transporterId
        );

        if (updatedTransporters.length > 0) {
          nextPayload[enquiryId] = updatedTransporters;
        } else {
          delete nextPayload[enquiryId];
        }

        // Remove remark if unchecked
        setRemarks((prevRemarks) => {
          const nextRemarks = { ...prevRemarks };
          delete nextRemarks[key];
          return nextRemarks;
        });
      }

      return nextPayload;
    });
  };

  const remarkChangeHandler = (e, enquiryId, transporterId) => {
    const key = `${enquiryId}_${transporterId}`;
    const value = e.target.value;
    setRemarks((prev) => ({
      ...prev,
      [key]: value,
    }));
  };



  const ClickHandler = () => {
    if (Object.keys(finalPayload).length === 0) {
      toast.error("Please select at least one transporter");
      return;
    }

    TrackMixPanel({
      ModuleName: "lorri spot",
      ScreenName: "my enquiries - request approval",
      Data: `enq ids: ${JSON.stringify(finalPayload)}`,
      Definition: "user has clicked on request approval submit button",
    });

    Activity([
      {
        screen: "LoRRI Adhoc",
        module: "Request Approval",
        user_activity: "Approval requested",
        timestamp: currentRequestTimeStamp(),
        "userEmail": userDetails?.email, "userName": userDetails?.user_name, "companyname": userDetails.company_name
          ? userDetails.company_name
          : userDetails.email
            ? userDetails.email.split("@")[1].split(".")[0]
            : ""
      },
    ]);

    const formattedPayload = {};
    Object.keys(finalPayload).forEach((enqId) => {
      formattedPayload[enqId] = finalPayload[enqId].map((tId) => ({
        transporter_id: tId,
        remark: remarks[`${enqId}_${tId}`] || "",
      }));
    });

    Axios.put(`${Urls.spot}?field=request_bulk_approve_v2`, {
      enquiry_ids: formattedPayload,
    })
      .then((res) => {
        if (res.data?.status === "error") {
          toast.info(res.data?.message);
          return;
        }
        if (res.data?.results && Array.isArray(res.data.results)) {
          const failures = res.data.results.filter((r) => r.status === "failed");
          failures.forEach((f) => {
            toast.error(
              `${f.display_adhoc_id || f.truck_enquiry_id}: ${f.reason}`
            );
          });
          if (failures.length === res.data.results.length) return;
        }
        toast.success("Approval Requested");
        document.getElementById("close_contactus")?.click();
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const isChecked = (item, transporter) => {
    return !!(
      finalPayload &&
      finalPayload[item.truck_enquiry_id] &&
      finalPayload[item.truck_enquiry_id].includes(transporter.transporter_id)
    );
  };

  const isAllChecked = (item, matchingTransporters) => {
    return (
      matchingTransporters.length > 0 &&
      matchingTransporters.every((transporter) => isChecked(item, transporter))
    );
  };

  const toggleAllForEnquiry = (e, item, matchingTransporters) => {
    const enquiryId = item.truck_enquiry_id;
    const checked = e.target.checked;

    setFinalPayload((prevPayload) => {
      const nextPayload = { ...prevPayload };
      if (checked) {
        nextPayload[enquiryId] = matchingTransporters.map((t) => t.transporter_id);
      } else {
        delete nextPayload[enquiryId];
      }
      return nextPayload;
    });

    if (!checked) {
      setRemarks((prevRemarks) => {
        const nextRemarks = { ...prevRemarks };
        matchingTransporters.forEach((transporter) => {
          delete nextRemarks[`${enquiryId}_${transporter.transporter_id}`];
        });
        return nextRemarks;
      });
    }
  };

  const renderDesktopList = () => (
    <>
      <div className={styles.desktopHeaderRow}>
        <HeaderCell width="4%">Select</HeaderCell>
        <HeaderCell width="3%"></HeaderCell>
        <HeaderCell width="6%">User</HeaderCell>
        <HeaderCell width="12%">Adhoc ID</HeaderCell>
        <HeaderCell width="16%">Lane</HeaderCell>
        <HeaderCell width="9%">Truck Type</HeaderCell>
        <HeaderCell width="5%" className={styles.headerCellNoWrap}># Trucks</HeaderCell>
        <HeaderCell width="8%">Contract tenure</HeaderCell>
        <HeaderCell width="8%">Date of Placement</HeaderCell>
        <HeaderCell width="12%">Transporter</HeaderCell>
        <HeaderCell width="12%">Rate for Approval</HeaderCell>
      </div>

      {approvalList.map((item, index) => {
        const matchingTransporters = getMatchingTransporters(item);
        if (matchingTransporters.length === 0) return null;

        const {
          originName,
          destName,
          truckLabel,
          truck_required,
          contract_tenure,
          due_date,
        } = getLaneData(item);
        const userEmail = item.email ? titleCase(item.email.split("@")[0]) : "User";
        const adhocId = item.display_adhoc_id || "NA";
        const totalQuoted = matchingTransporters.length;
        const approvableCount = matchingTransporters.filter(isApprovableTransporter).length;
        const isExpanded = expandedRows.has(item.truck_enquiry_id);
        const topTransporter = matchingTransporters[0];
        const topLastRate = getLastRate(topTransporter);
        const topRate = topLastRate?.rate !== undefined && topLastRate?.rate !== null
          ? topLastRate.rate.toLocaleString()
          : "NA";

        return (
          <div
            key={`enquiry${item.truck_enquiry_id || index}`}
            className={styles.enquiryRow}
          >
            {/* ── Summary row ── */}
            <div
              className={`${styles.summaryRow} ${isExpanded ? styles.summaryRowExpanded : ""}`}
              onClick={() => toggleExpand(item.truck_enquiry_id)}
            >
              <div className={styles.selectCell} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isAllChecked(item, matchingTransporters)}
                  className={styles.checkbox}
                  onChange={(e) => toggleAllForEnquiry(e, item, matchingTransporters)}
                />
              </div>
              <div className={styles.chevronCell}>
                <i
                  className={`fa fa-chevron-${isExpanded ? "down" : "right"} ${styles.chevronIcon}`}
                />
              </div>
              <div className={styles.userCell}>
                {userEmail}
              </div>
              <div className={styles.adhocIdCell}>
                <div>{adhocId}</div>
                {item.indent_created === true && (
                  <span className={styles.indentBadge}>
                    Indent Created
                  </span>
                )}
                <div className={styles.quotesSummary}>
                  {approvableCount} of {totalQuoted} quotes accepted
                </div>
              </div>
              <div className={styles.laneCell}>
                {originName !== "NA" && (
                  <>
                    <i className={`fa fa-map-marker ${styles.laneMarkerIcon}`} />
                    <span>{originName}</span>
                  </>
                )}
                {" → "}
                {destName !== "NA" && (
                  <>
                    <i className={`fa fa-map-marker ${styles.laneMarkerIcon}`} />
                    <span>{destName}</span>
                  </>
                )}
              </div>
              <div className={styles.truckTypeCell}>
                {truckLabel}
              </div>
              <div className={styles.trucksCountCell}>
                {truck_required || "NA"}
              </div>
              <div className={styles.tenureCell}>
                {getTenure(contract_tenure)}
              </div>
              <div className={styles.dateCell}>
                {due_date}
              </div>
              <div className={styles.transporterCell}>
                <button
                  type="button"
                  onClick={(e) => handleOpenProfile(e, topTransporter)}
                  className={styles.transporterLinkButton}
                >
                  {topTransporter.transporter_name}
                </button>
              </div>
              <div className={styles.rateCell}>
                {topRate}
              </div>
            </div>

            {/* ── Expanded panel: ranked transporter list ── */}
            {isExpanded && (
              <div className={styles.expandedPanel}>
                <div className={styles.expandedHeaderRow}>
                  <div className={styles.expandedHeaderSelect}></div>
                  <div className={styles.expandedHeaderRank}>Rank</div>
                  <div className={styles.expandedHeaderTransporter}>Transporter</div>
                  <div className={styles.expandedHeaderQuote}>Quote</div>
                  <div className={styles.expandedHeaderStatus}>Status</div>
                  <div className={styles.expandedHeaderAcceptedBy}>Accepted By</div>
                  <div className={styles.expandedHeaderRemark}>Remark</div>
                </div>
                {matchingTransporters.map((transporter, i) => {
                  const lastRate = getLastRate(transporter);
                  const rate = lastRate?.rate !== undefined && lastRate?.rate !== null
                    ? lastRate.rate.toLocaleString()
                    : "NA";
                  const approvable = isApprovableTransporter(transporter);
                  const statusLabel = approvable
                    ? (lastRate?.bid_status || "Accepted")
                    : "Quoted";

                  return (
                    <div
                      key={`transporter_enquiry${transporter.transporter_id || i}`}
                      className={`${styles.transporterRow} ${i > 0 ? styles.transporterRowDivider : ""}`}
                    >
                      <div className={styles.transporterSelectCell}>
                        <input
                          type="checkbox"
                          checked={isChecked(item, transporter)}
                          className={styles.checkbox}
                          onChange={(e) => changeHandler(e, item, transporter)}
                        />
                      </div>
                      <div className={styles.rankCell}>
                        L{i + 1}
                      </div>
                      <div className={styles.transporterNameCell}>
                        <button
                          type="button"
                          onClick={(e) => handleOpenProfile(e, transporter)}
                          className={styles.transporterLinkButton}
                        >
                          {transporter.transporter_name}
                        </button>
                        {item.indent_created === true && transporter.is_awarded === true && (
                          <span className={styles.indentBadgeSmall}>
                            Indent Created
                          </span>
                        )}
                      </div>
                      <div className={styles.quoteCell}>
                        {rate}
                      </div>
                      <div
                        className={`${styles.statusCell} ${approvable ? styles.statusCellApprovable : styles.statusCellNonApprovable}`}
                      >
                        {statusLabel}
                      </div>
                      <div className={styles.acceptedByCell}>
                        {lastRate?.accepted_by || "NA"}
                      </div>
                      <div className={styles.remarkCell}>
                        <input
                          type="text"
                          className={`form-control form-control-sm ${styles.remarkInput}`}
                          placeholder="Add remark..."
                          value={remarks[`${item.truck_enquiry_id}_${transporter.transporter_id}`] || ""}
                          disabled={!isChecked(item, transporter)}
                          onChange={(e) => remarkChangeHandler(e, item.truck_enquiry_id, transporter.transporter_id)}
                        />
                        {lastRate?.remark && (
                          <div className={styles.remarkNote}>
                            <i>Note: {lastRate.remark}</i>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  const renderMobileList = () =>
    approvalList.map((item, index) => {
      const matchingTransporters = getMatchingTransporters(item);
      if (matchingTransporters.length === 0) return null;

      const { originName, destName, truckLabel, truck_required, due_date } = getLaneData(item);
      const totalQuoted = matchingTransporters.length;
      const approvableCount = matchingTransporters.filter(isApprovableTransporter).length;
      const isExpanded = expandedRows.has(item.truck_enquiry_id);

      return (
        <div
          key={`mob_${item.truck_enquiry_id || index}`}
          className={styles.mobileCard}
        >
          <div
            className={styles.mobileCardHeader}
            onClick={() => toggleExpand(item.truck_enquiry_id)}
          >
            <div className={styles.mobileHeaderTopRow}>
              <div className={styles.mobileHeaderLeft}>
                <input
                  type="checkbox"
                  checked={isAllChecked(item, matchingTransporters)}
                  className={styles.mobileCheckbox}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => toggleAllForEnquiry(e, item, matchingTransporters)}
                />
                <div className={styles.mobileLaneTitle}>
                  {originName} → {destName}
                </div>
              </div>
              <i
                className={`fa fa-chevron-${isExpanded ? "down" : "right"} ${styles.chevronIconMobile}`}
              />
            </div>
            <div className={styles.mobileMetaLine}>
              {item.display_adhoc_id || "NA"} | {truckLabel} | {truck_required} Trucks | {due_date}
            </div>
            {item.indent_created === true && (
              <span className={styles.indentBadgeMobile}>
                Indent Created
              </span>
            )}
            <div className={styles.mobileQuotesSummary}>
              {approvableCount} of {totalQuoted} quotes accepted
            </div>
          </div>

          {isExpanded && (
            <div className={styles.mobileExpandedPanel}>
              {matchingTransporters.map((transporter, i) => {
                const lastRate = getLastRate(transporter);
                const rate = lastRate?.rate !== undefined && lastRate?.rate !== null
                  ? lastRate.rate.toLocaleString()
                  : "NA";
                const remark = lastRate?.remark;
                const approvable = isApprovableTransporter(transporter);
                const statusLabel = approvable
                  ? (lastRate?.bid_status || "Accepted")
                  : "Quoted";

                return (
                  <div
                    key={`mob_trans_${transporter.transporter_id || i}`}
                    className={styles.mobileTransporterRow}
                  >
                    <div className={styles.mobileTransporterHeader}>
                      <input
                        type="checkbox"
                        checked={isChecked(item, transporter)}
                        onChange={(e) => changeHandler(e, item, transporter)}
                        className={styles.mobileTransporterCheckbox}
                      />
                      <span className={styles.mobileRankLabel}>
                        L{i + 1}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => handleOpenProfile(e, transporter)}
                        className={styles.transporterLinkButtonBold}
                      >
                        {transporter.transporter_name}
                      </button>
                      {item.indent_created === true && transporter.is_awarded === true && (
                        <span className={styles.indentBadgeSmall}>
                          Indent Created
                        </span>
                      )}
                    </div>
                    <div className={styles.mobileRateLine}>
                      ₹{rate}
                    </div>
                    <div className={`${styles.mobileStatusLine} ${approvable ? styles.mobileStatusLineApprovable : styles.mobileStatusLineNonApprovable}`}>
                      Status: {statusLabel} | Accepted by: {lastRate?.accepted_by || "NA"}
                    </div>
                    {remark && (
                      <div className={styles.mobileRemarkNote}>
                        <i>Note: {remark}</i>
                      </div>
                    )}
                    <div className={styles.mobileRemarkInputWrapper}>
                      <input
                        type="text"
                        className={`form-control form-control-sm ${styles.remarkInputMobile}`}
                        placeholder="Add remark..."
                        value={remarks[`${item.truck_enquiry_id}_${transporter.transporter_id}`] || ""}
                        disabled={!isChecked(item, transporter)}
                        onChange={(e) => remarkChangeHandler(e, item.truck_enquiry_id, transporter.transporter_id)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });

  return (
    <>
      <Loader show={show} />
      {requestapprovallist.length > 0 ? (
        <div>
          <div
            className={`${styles.infoBanner} ${isMobile() ? styles.infoBannerMobile : styles.infoBannerDesktop}`}
          >
            <i className={`fa fa-info-circle ${styles.infoIcon}`} />
            <span>Only accepted quotes can be approved by the approver</span>
          </div>

          <div className={styles.scrollList}>
            {approvalList && approvalList.length > 0 ? (
              !isMobile() ? renderDesktopList() : renderMobileList()
            ) : (
              show === false && (
                <center className="pb-5">
                  <DataGhost
                    title="Looks like you have no enquiries for request approval"
                    hasSubtitle={false}
                  />
                </center>
              )
            )}
          </div>

          <center className={styles.submitButtonWrapper}>
            <button
              className="btn btn-sm btn-primary"
              disabled={Object.keys(finalPayload).length === 0}
              onClick={() => ClickHandler()}
            >
              Request Approval
            </button>
          </center>
        </div>
      ) : (
        <div>Please select enquiries</div>
      )}

      {openProfileModal && activeTransporter?.id !== "" && (
        <NewProfileModal
          isOpen={openProfileModal}
          onClose={handleCloseProfileModal}
          transporter_id={activeTransporter.id}
          transporter_name={activeTransporter.name}
          screenName="SPOT - Request Approval"
          moduleName="SPOT - Request Approval"
        />
      )}
    </>
  );
};

export default RequestApproval;